import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { animate, query, stagger, style, transition, trigger } from '@angular/animations';

interface FeaturedCar {
  name: string;
  brand: string;
  launchDate: string;
  price: string;
  image: string;
}

interface FeatureCard {
  title: string;
  description: string;
  icon: string;
}

interface StatItem {
  value: number;
  suffix: string;
  label: string;
  current: number;
}

interface StoryStep {
  title: string;
  label: string;
  description: string;
  image: string;
}

interface Testimonial {
  quote: string;
  name: string;
  role: string;
}

interface ConciergeOption {
  key: string;
  icon: string;
  label: string;
  hint: string;
  match: string;
  model: string;
  reason: string;
  themeClass: string;
  cta: string;
  metrics: Array<{ label: string; value: string }>;
}

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.css'],
  animations: [
    trigger('pageEnter', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('500ms ease-out', style({ opacity: 1 })),
        query(
          '.motion-reveal, .featured-card, .feature-card, .stat-card',
          [
            style({ opacity: 0, transform: 'translateY(28px)' }),
            stagger(90, [animate('520ms cubic-bezier(0.16, 1, 0.3, 1)', style({ opacity: 1, transform: 'translateY(0)' }))])
          ],
          { optional: true }
        )
      ])
    ])
  ]
})
export class HomeComponent implements OnInit, OnDestroy {
  @ViewChild('heroVideoElement', { static: true }) heroVideoElement: ElementRef<HTMLVideoElement>;
  @ViewChild('showcase', { static: false }) showcase: ElementRef;
  @ViewChild('statsSection', { static: false }) statsSection: ElementRef;
  @ViewChild('storySection', { static: false }) storySection: ElementRef;

  heroVideo = 'https://videos.pexels.com/video-files/854671/854671-hd_1920_1080_25fps.mp4';
  heroPoster = '/assets/img/cover.jpeg';

  cursorX = 50;
  cursorY = 50;
  showcaseTiltX = 0;
  showcaseTiltY = 0;
  scrollProgress = 0;
  activeStoryIndex = 0;
  activeTestimonialIndex = 0;
  activeConciergeKey = 'range';
  conciergePointerX = 0;
  conciergePointerY = 0;
  shortlistOpen = false;
  countersStarted = false;
  private testimonialTimer: any;
  private counterTimer: any;

  stats: StatItem[] = [
    { value: 18, suffix: 'K+', label: 'active vehicle leads', current: 0 },
    { value: 92, suffix: '%', label: 'faster shortlisting', current: 0 },
    { value: 240, suffix: '+', label: 'launch signals tracked', current: 0 }
  ];

  featuredCars: FeaturedCar[] = [
    {
      name: 'Taycan Turbo GT',
      brand: 'Porsche',
      launchDate: 'Q4 2026',
      price: '$231,995',
      image: '/assets/img/Taycan Turbo GT.jpg'
    },
    {
      name: 'Model Y Juniper',
      brand: 'Tesla',
      launchDate: 'Summer 2026',
      price: '$44,990',
      image: '/assets/img/Model Y Juniper.jpg'
    },
    {
      name: 'Rivian R2',
      brand: 'Rivian',
      launchDate: 'Early 2027',
      price: '$45,000',
      image: '/assets/img/The Rivian R2.webp'
    }
  ];

  featureCards: FeatureCard[] = [
    {
      title: 'Latest Vehicle Pipeline',
      description: 'Track enquiries, launch timelines, trims, prices, and availability in one premium workspace.',
      icon: '01'
    },
    {
      title: 'EV Insights',
      description: 'Understand range, charging speed, battery options, incentives, and real-world ownership details.',
      icon: '02'
    },
    {
      title: 'Vehicle Comparison',
      description: 'Compare performance, comfort, technology, safety, and total value before shortlisting a model.',
      icon: '03'
    },
    {
      title: 'Expert Reviews',
      description: 'Read curated editorial guidance that translates automotive data into confident buying decisions.',
      icon: '04'
    }
  ];

  fleetCards = [
    {
      model: 'Taycan Turbo GT',
      type: 'Porsche premium EV performance lead',
      stat: '96%',
      label: 'buyer intent',
      image: '/assets/img/Taycan Turbo GT.jpg',
      fallbackImage: '/assets/img/sports-car.jpeg'
    },
    {
      model: 'Model Y Juniper',
      type: 'Tesla family SUV enquiry',
      stat: '12',
      label: 'stock matches',
      image: '/assets/img/Model Y Juniper.jpg',
      fallbackImage: '/assets/img/car_0.jpg'
    },
    {
      model: 'Rivian R2',
      type: 'Adventure EV buyer',
      stat: '4m',
      label: 'response SLA',
      image: '/assets/img/The Rivian R2.webp',
      fallbackImage: '/assets/img/sidebar-4.jpg'
    }
  ];

  storySteps: StoryStep[] = [
    {
      title: 'Luxury Sedan',
      label: 'Step 01',
      description: 'Executive proportions, silent cabins, and precision-tuned performance for refined daily drives.',
      image: '/assets/img/cover.jpeg'
    },
    {
      title: 'Performance SUV',
      label: 'Step 02',
      description: 'Commanding road presence, adaptive traction, and family-ready space without sacrificing emotion.',
      image: '/assets/img/sidebar-1.jpg'
    },
    {
      title: 'Electric GT',
      label: 'Step 03',
      description: 'Instant torque, next-generation batteries, and connected charging intelligence for long journeys.',
      image: '/assets/img/sports-car.jpeg'
    },
    {
      title: 'Future Concept',
      label: 'Step 04',
      description: 'Autonomous-ready design, immersive glass cockpits, and sculptural forms from tomorrow’s roads.',
      image: '/assets/img/sidebar-3.jpg'
    }
  ];

  conciergeOptions: ConciergeOption[] = [
    {
      key: 'range',
      icon: '⚡',
      label: 'Maximum range',
      hint: 'Long-distance EV comfort',
      match: '97%',
      model: 'Porsche Taycan Turbo GT',
      reason: 'Best for drivers who want fast charging confidence, luxury appointments, and a launch plan that feels truly premium.',
      themeClass: 'range',
      cta: 'Open performance EV shortlist',
      metrics: [
        { label: 'Launch window', value: 'Q4 2026' },
        { label: 'Buyer fit', value: 'Performance EV' },
        { label: 'Priority', value: 'Charging' }
      ]
    },
    {
      key: 'family',
      icon: '◈',
      label: 'Family utility',
      hint: 'Smart space and safety',
      match: '94%',
      model: 'Tesla Model Y Juniper',
      reason: 'A practical shortlist leader for shoppers balancing connected tech, flexible cargo room, and approachable EV pricing.',
      themeClass: 'family',
      cta: 'Open family SUV shortlist',
      metrics: [
        { label: 'Launch window', value: 'Summer 2026' },
        { label: 'Buyer fit', value: 'Family SUV' },
        { label: 'Priority', value: 'Space' }
      ]
    },
    {
      key: 'adventure',
      icon: '↗',
      label: 'Adventure ready',
      hint: 'Weekend capability',
      match: '91%',
      model: 'Rivian R2',
      reason: 'Ideal for outdoor-focused buyers who want rugged utility, approachable pricing, and a clear reservation journey.',
      themeClass: 'adventure',
      cta: 'Open adventure EV shortlist',
      metrics: [
        { label: 'Launch window', value: 'Early 2027' },
        { label: 'Buyer fit', value: 'Adventure EV' },
        { label: 'Priority', value: 'Capability' }
      ]
    }
  ];

  testimonials: Testimonial[] = [
    {
      quote: 'Carmotive CRM makes every buyer journey feel cinematic. I can compare EV leads quickly and know exactly what to prioritize.',
      name: 'Aarav Mehta',
      role: 'EV buyer'
    },
    {
      quote: 'The comparison flow feels more premium than most manufacturer pages. It saves hours of research.',
      name: 'Maya Kapoor',
      role: 'Luxury SUV shopper'
    },
    {
      quote: 'I use the upcoming launch trackers weekly. The experience is fast, beautiful, and surprisingly practical.',
      name: 'Rohan Shah',
      role: 'Automotive enthusiast'
    }
  ];


  useFallbackImage(event: Event, fallbackImage: string): void {
    const image = event.target as HTMLImageElement;
    if (image && image.src.indexOf(fallbackImage) === -1) {
      image.src = fallbackImage;
    }
  }


  constructor() {}

  ngOnInit(): void {
    this.enforceHeroVideoSilence();
    this.testimonialTimer = setInterval(() => {
      this.activeTestimonialIndex = (this.activeTestimonialIndex + 1) % this.testimonials.length;
    }, 5000);
  }

  ngOnDestroy(): void {
    if (this.testimonialTimer) {
      clearInterval(this.testimonialTimer);
    }
    if (this.counterTimer) {
      clearInterval(this.counterTimer);
    }
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    this.updateScrollProgress();
    this.updateActiveStory();
    this.startCountersWhenVisible();
  }


  enforceHeroVideoSilence(): void {
    if (!this.heroVideoElement) {
      return;
    }
    var video = this.heroVideoElement.nativeElement;
    video.muted = true;
    video.defaultMuted = true;
    video.volume = 0;
  }

  onHeroPointerMove(event: MouseEvent): void {
    var width = window.innerWidth || 1;
    var height = window.innerHeight || 1;
    this.cursorX = Math.round((event.clientX / width) * 100);
    this.cursorY = Math.round((event.clientY / height) * 100);
  }

  onShowcasePointerMove(event: MouseEvent): void {
    if (!this.showcase) {
      return;
    }
    var rect = this.showcase.nativeElement.getBoundingClientRect();
    var x = (event.clientX - rect.left) / rect.width - 0.5;
    var y = (event.clientY - rect.top) / rect.height - 0.5;
    this.showcaseTiltX = Number((-y * 8).toFixed(2));
    this.showcaseTiltY = Number((x * 10).toFixed(2));
  }

  resetShowcaseTilt(): void {
    this.showcaseTiltX = 0;
    this.showcaseTiltY = 0;
  }

  getHeroCursorStyle(): { [key: string]: string } {
    return {
      '--cursor-x': this.cursorX + '%',
      '--cursor-y': this.cursorY + '%'
    };
  }

  getShowcaseStyle(): { [key: string]: string } {
    return {
      '--tilt-x': this.showcaseTiltX + 'deg',
      '--tilt-y': this.showcaseTiltY + 'deg',
      '--showcase-zoom': (1 + this.scrollProgress * 0.12).toFixed(3)
    };
  }

  get activeConcierge(): ConciergeOption {
    return this.conciergeOptions.find((option) => option.key === this.activeConciergeKey) || this.conciergeOptions[0];
  }

  getConciergeOrbitStyle(): { [key: string]: string } {
    var activeIndex = this.conciergeOptions.findIndex((option) => option.key === this.activeConciergeKey);
    var safeIndex = Math.max(0, activeIndex);
    return {
      '--orbit-hue': safeIndex === 0 ? '82deg' : safeIndex === 1 ? '196deg' : '28deg',
      '--orbit-depth': safeIndex === 0 ? '0deg' : safeIndex === 1 ? '112deg' : '224deg',
      '--orbit-pointer-x': this.conciergePointerX + 'deg',
      '--orbit-pointer-y': this.conciergePointerY + 'deg'
    };
  }

  setConcierge(key: string): void {
    this.activeConciergeKey = key;
  }

  onConciergePointerMove(event: MouseEvent): void {
    var target = event.currentTarget as HTMLElement;
    var rect = target.getBoundingClientRect();
    var x = (event.clientX - rect.left) / rect.width - 0.5;
    var y = (event.clientY - rect.top) / rect.height - 0.5;
    this.conciergePointerX = Number((-y * 10).toFixed(2));
    this.conciergePointerY = Number((x * 14).toFixed(2));
  }

  resetConciergePointer(): void {
    this.conciergePointerX = 0;
    this.conciergePointerY = 0;
  }

  openShortlist(): void {
    this.shortlistOpen = true;
  }

  closeShortlist(): void {
    this.shortlistOpen = false;
  }

  setTestimonial(index: number): void {
    this.activeTestimonialIndex = index;
    if (this.testimonialTimer) {
      clearInterval(this.testimonialTimer);
    }
  }

  private updateScrollProgress(): void {
    if (!this.showcase) {
      this.scrollProgress = 0;
      return;
    }
    var rect = this.showcase.nativeElement.getBoundingClientRect();
    var viewport = window.innerHeight || document.documentElement.clientHeight;
    var rawProgress = (viewport - rect.top) / (viewport + rect.height);
    this.scrollProgress = Math.max(0, Math.min(1, rawProgress));
  }

  private updateActiveStory(): void {
    if (!this.storySection || !this.storySteps.length) {
      return;
    }
    var rect = this.storySection.nativeElement.getBoundingClientRect();
    var viewport = window.innerHeight || document.documentElement.clientHeight;
    var totalScrollable = Math.max(1, rect.height - viewport);
    var progress = Math.max(0, Math.min(1, -rect.top / totalScrollable));
    var index = Math.floor(progress * this.storySteps.length);
    this.activeStoryIndex = Math.min(this.storySteps.length - 1, index);
  }

  private startCountersWhenVisible(): void {
    if (this.countersStarted || !this.statsSection) {
      return;
    }
    var rect = this.statsSection.nativeElement.getBoundingClientRect();
    var viewport = window.innerHeight || document.documentElement.clientHeight;
    if (rect.top > viewport * 0.78) {
      return;
    }
    this.countersStarted = true;
    var frame = 0;
    var frames = 70;
    this.counterTimer = setInterval(() => {
      frame += 1;
      var eased = 1 - Math.pow(1 - frame / frames, 3);
      this.stats = this.stats.map((stat) => {
        return {
          value: stat.value,
          suffix: stat.suffix,
          label: stat.label,
          current: Math.round(stat.value * Math.min(1, eased))
        };
      });
      if (frame >= frames && this.counterTimer) {
        clearInterval(this.counterTimer);
      }
    }, 18);
  }
}
